import { DeviceStatus } from '@/api/deviceApi'
import { AccessDeniedFallback, RouteAccessBoundary } from '@/components/access-control'
import { CardGridSkeleton, PageShellSkeleton } from '@/components/ui/skeletons'
import { resolveQuotaTenantId } from '@/hooks/access-control/resolveQuotaTenantId'
import { useDisplayPreference } from '@/hooks/useDisplayPreference'
import {
	authenticatedRouteGuard,
	consoleOnlyRouteGuard,
	getProjectFallbackTarget,
	organizationLevelRouteGuard,
	platformOnlyRouteGuard,
	prefetchQuota,
	projectSettingsAdminRouteGuard,
	redirectToFirstOrg,
} from '@/lib/routeGuards'
import { getDefaultRouteForUser } from '@/lib/routerUtils'
import {
	accessDeniedSearchSchema,
	dashboardSearchSchema,
	dbcParsersSearchSchema,
	deviceSearchSchema,
	sessionRulesSearchSchema,
	streamsSearchSchema,
	subscriptionPlansSearchSchema,
} from '@/lib/searchSchemas'
import { isPlatformUser } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useProjectsStore } from '@/stores/projectsStore'
import type { MetersModule } from '@/types/organization'
import { isConsoleDeployment, isDemoDeployment, isPlatformDeployment } from '@/utils/deployment'

import {
	Outlet,
	createRootRoute,
	createRoute,
	createRouter,
	lazyRouteComponent,
	redirect,
} from '@tanstack/react-router'
import { z } from 'zod'
import { RootLayout } from '../layouts/RootLayout'
import { AccessDeniedPage } from '../pages/AccessDeniedPage'
// TEMPORARY — manual visual QA route for the PanelContainer failedColumns banner.
// Delete this import + panelContainerPreviewRoute + its addChildren entry below when done.
import { PanelContainerPreview } from '../pages/Dev/PanelContainerPreview'
import { DiagnosticsPage } from '../pages/Diagnostics/DiagnosticsPage'
import { LoginPage } from '../pages/LoginPage'
import { NotFoundPage } from '../pages/NotFoundPage'
import { METERS_MODULE_IDS } from '../pages/Organizations/Meters/metersConfig'

// Code-split the heavy page routes: each becomes its own chunk fetched on navigation
// (and never fetched for users blocked by RouteAccessBoundary). The shell, login, and
// the small not-found/access-denied pages stay eager for fast first paint.
const ActionSettingsPage = lazyRouteComponent(() => import('../pages/Actions/ActionSettingsPage'), 'ActionSettingsPage')
const AssetsPage = lazyRouteComponent(() => import('../pages/Actions/AssetsPage'), 'AssetsPage')
const OtaCampaignsPage = lazyRouteComponent(() => import('../pages/Actions/OtaCampaignsPage'), 'OtaCampaignsPage')
const RemoteCommandsPage = lazyRouteComponent(() => import('../pages/Actions/RemoteCommandsPage'), 'RemoteCommandsPage')
const RulesPage = lazyRouteComponent(() => import('../pages/Actions/RulesPage'), 'RulesPage')
const AuditLogsPage = lazyRouteComponent(() => import('../pages/AuditLogsPage'), 'AuditLogsPage')
const DashboardDisplayPage = lazyRouteComponent(
	() => import('../pages/Dashboards/DashboardDisplayPage'),
	'DashboardDisplayPage',
)
const DashboardsPage = lazyRouteComponent(() => import('../pages/DashboardsPage'), 'DashboardsPage')
const DbcParserManagementPage = lazyRouteComponent(
	() => import('../pages/DbcParserManagementPage'),
	'DbcParserManagementPage',
)
const DeviceManagementPage = lazyRouteComponent(() => import('../pages/DeviceManagementPage'), 'DeviceManagementPage')
const ExportersPage = lazyRouteComponent(() => import('../pages/Exporters/ExportersPage'))
const OrganizationMetersLayout = lazyRouteComponent(
	() => import('../pages/Organizations/Meters/MetersPage'),
	'OrganizationMetersLayout',
)
const OrganizationMetersList = lazyRouteComponent(
	() => import('../pages/Organizations/Meters/MetersPage'),
	'OrganizationMetersList',
)
const OrganizationBillingPage = lazyRouteComponent(() => import('../pages/Organizations/OrganizationBillingPage'))
const SessionRulesPage = lazyRouteComponent(() => import('../pages/SessionRulesPage'), 'SessionRulesPage')
const SessionsFeedPage = lazyRouteComponent(() => import('../pages/SessionsFeedPage'), 'SessionsFeedPage')
const ApiKeysSettingsPage = lazyRouteComponent(() => import('../pages/Settings/ApiKeysSettingsPage'))
const MetadataPage = lazyRouteComponent(() => import('../pages/Settings/Metadata/MetadataPage'))
const OrganizationGeneralPage = lazyRouteComponent(
	() => import('../pages/Settings/OrganizationGeneralPage'),
	'OrganizationGeneralPage',
)
const ProjectGeneralPage = lazyRouteComponent(
	() => import('../pages/Settings/ProjectGeneralPage'),
	'ProjectGeneralPage',
)
const QuotasPage = lazyRouteComponent(() => import('../pages/Settings/Quotas/QuotasPage'), 'QuotasPage')
const RoleSettingsPage = lazyRouteComponent(() => import('../pages/Settings/Role/RoleSettingsPage'))
const SettingsPage = lazyRouteComponent(() => import('../pages/Settings/SettingsPage'), 'SettingsPage')
const UsersSettingsPage = lazyRouteComponent(() => import('../pages/Settings/UsersSettingsPage'))
const StreamsPage = lazyRouteComponent(() => import('../pages/StreamsPage'))
const SubscriberGroupsPage = lazyRouteComponent(() => import('../pages/SubscriberGroups/SubscriberGroupsPage'))
const SystemNotificationsPage = lazyRouteComponent(
	() => import('../pages/SystemNotifications/SystemNotificationsPage'),
	'SystemNotificationsPage',
)
const TerminalPage = lazyRouteComponent(() => import('../pages/TerminalPage'), 'TerminalPage')
const WelcomePage = lazyRouteComponent(() => import('../pages/WelcomePage'), 'WelcomePage')

// Create a root route
const rootRoute = createRootRoute({
	validateSearch: z
		.object({
			filters: z.string().optional(),
		})
		.passthrough(),
	component: RootLayout,
	// Adding authentication context to the root route
	beforeLoad: () => {
		return {
			isAuthenticated: useAuthStore.getState().isAuthenticated,
		}
	},
})

// Create an index route that redirects to the project route based on permissions
const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/',
	beforeLoad: ({ context, location }) => {
		authenticatedRouteGuard(context, { location })

		const authState = useAuthStore.getState()
		const isAuthenticated = authState.isAuthenticated

		if (isAuthenticated) {
			if (isConsoleDeployment() || isDemoDeployment()) {
				const user = authState.user
				if (user?.organizations?.length && user.organizations.length > 0) {
					throw redirectToFirstOrg()
				}
				throw redirect({ to: '/welcome' })
			}
			// Platform deployment
			throw redirectToFirstOrg()
		}
		throw redirect({ to: '/login' })
	},
})

// Login route
const loginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/login',
	component: () => <LoginPage />,
	beforeLoad: ({ context, location }) => {
		// Redirect to the home page if the user is already authenticated
		if (context.isAuthenticated) {
			// Check if there's a redirect parameter from auth guard
			const redirectTo = (location.search as { redirect?: string })?.redirect
			if (redirectTo && redirectTo !== '/login') {
				throw redirect({
					to: redirectTo,
					replace: true,
				})
			}
			throw redirect({
				to: '/',
				replace: true,
			})
		}
	},
})

// Welcome route — plan selection / onboarding when user has no org or tenant access
const welcomeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/welcome',
	component: () => <WelcomePage />,
	beforeLoad: ({ context, location }) => {
		authenticatedRouteGuard(context, { location })
		if (isPlatformDeployment()) {
			throw redirectToFirstOrg()
		}
	},
})

// Define validation schemas
const tenantParamsSchema = z.object({
	tenantId: z.string().min(1, 'Project ID is required'),
})

const orgIdParamsSchema = z.object({
	orgId: z.string().min(1, 'Organization ID is required'),
})

const statusParamsSchema = z.object({
	status: z.enum(['active', 'inactive', 'all']),
})

// Organization-scoped route - parent for organization-specific pages
const organizationRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/orgs/$orgId',
	parseParams: params => orgIdParamsSchema.parse(params),
	component: () => <Outlet />,
	beforeLoad: ({ context, params, location }) => {
		authenticatedRouteGuard(context, { location })
		const user = useAuthStore.getState().user
		if (!user) {
			throw redirectToFirstOrg()
		}
		const hasAccess =
			user.organizations?.some(org => org.id === params.orgId) ||
			(isPlatformUser(user) && user.tenant_list?.some(t => t.org_id === params.orgId)) ||
			false

		if (!hasAccess) {
			throw redirectToFirstOrg()
		}

		useAuthStore.getState().setCurrentOrganizationId(params.orgId)
	},
})

// Organization project route - parent for all project-specific pages
const organizationProjectRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/projects/$tenantId',
	parseParams: params => {
		const { tenantId } = params
		return { tenantId: tenantParamsSchema.parse({ tenantId }).tenantId }
	},
	component: () => <Outlet />,
	beforeLoad: ({ context, params, location }) => {
		authenticatedRouteGuard(context, { location })
		if (!params.tenantId || params.tenantId.trim() === '') {
			throw redirectToFirstOrg()
		}

		const state = useAuthStore.getState()
		const user = state.user
		const platformUser = user && isPlatformUser(user) ? user : null

		if (platformUser) {
			const hasTenantAccess = platformUser.tenants?.includes(params.tenantId)
			if (!hasTenantAccess) {
				const fallback = getProjectFallbackTarget(platformUser, params.orgId, params.tenantId)
				if (fallback.to === '/orgs/$orgId/projects/$tenantId') {
					throw redirect({
						to: fallback.to,
						params: fallback.params,
						replace: true,
					})
				}
				if (fallback.to === '/orgs/$orgId/inventory/$status') {
					throw redirect({
						to: fallback.to,
						params: fallback.params,
						replace: true,
					})
				}
				if (fallback.to === '/access-denied') {
					throw redirect({
						to: fallback.to,
						search: fallback.search,
						replace: true,
					})
				}
			}
		}

		useAuthStore.getState().setCurrentTenant(params.tenantId)
	},
	// Prime the quota cache so access-control hooks have fresh data on first render.
	loader: ({ params }) => prefetchQuota(params.tenantId),
})

const organizationBillingRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/billing',
	component: () => <Outlet />,
	beforeLoad: ({ context }) => {
		// Billing is only available on Console deployments
		consoleOnlyRouteGuard(context)
	},
})

const organizationBillingIndexRoute = createRoute({
	getParentRoute: () => organizationBillingRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/billing/$type',
			params: { orgId: params.orgId, type: 'plan-details' },
			search,
			replace: true,
		})
	},
})

const PaymentTypeSchema = z.object({
	type: z.enum(['plan-details', 'payment-history']),
})

const organizationBillingTypeRoute = createRoute({
	getParentRoute: () => organizationBillingRoute,
	path: '/$type',
	parseParams: params => PaymentTypeSchema.parse(params),
	validateSearch: subscriptionPlansSearchSchema,
	component: () => <OrganizationBillingPage />,
})

// TEMPORARY — manual visual QA for the PanelContainer failedColumns banner. No auth guard,
// not linked from any nav; reachable only by typing the URL. Delete along with the import above
// and the addChildren entry below once done.
const panelContainerPreviewRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/dev/panel-container-preview',
	component: () => <PanelContainerPreview />,
})

// Catch-all route for 404
const notFoundRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '*',
	component: () => <NotFoundPage />,
})

// Homepage under organization project route
const projectHomeRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/',
	component: () => <AccessDeniedFallback />,
	beforeLoad: ({ context, params, location }) => {
		authenticatedRouteGuard(context)

		const defaultRoute = getDefaultRouteForUser(params.tenantId, params.orgId)

		const normalizePath = (path: string) => {
			if (path === '/') {
				return '/'
			}
			return path.replace(/\/+$/, '') || '/'
		}
		const normalizedCurrentPath = normalizePath(location.pathname)
		const normalizedDefaultRoute = defaultRoute ? normalizePath(defaultRoute) : null

		if (defaultRoute && normalizedDefaultRoute && normalizedDefaultRoute !== normalizedCurrentPath) {
			throw redirect({ to: defaultRoute, replace: true })
		}
	},
})

// Named access-denied page — org-ownership and bootstrap edge cases only.
const accessDeniedRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/access-denied',
	validateSearch: accessDeniedSearchSchema,
	component: () => <AccessDeniedPage />,
})

/** Org-scoped when no project (tenant) id is available for the project access-denied URL. */
const organizationAccessDeniedRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/access-denied',
	validateSearch: accessDeniedSearchSchema,
	component: () => <AccessDeniedPage />,
})

const globalAccessDeniedRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/access-denied',
	validateSearch: accessDeniedSearchSchema,
	component: () => <AccessDeniedPage />,
})

// Create main routes and their sub-routes
// DEVICE MANAGEMENT (under Device Management section)
const devicesRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/device-management',
	component: () => (
		<RouteAccessBoundary resource='devices'>
			<Outlet />
		</RouteAccessBoundary>
	),
})

const devicesIndexRoute = createRoute({
	getParentRoute: () => devicesRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/device-management/$status',
			params: { orgId: params.orgId, tenantId: params.tenantId, status: 'all' },
			search,
			replace: true,
		})
	},
})

// Device management with status parameter
const devicesStatusRoute = createRoute({
	getParentRoute: () => devicesRoute,
	path: '/$status',
	parseParams: params => statusParamsSchema.parse(params),
	validateSearch: deviceSearchSchema,
	component: () => <DeviceManagementPage />,
})

// Stream type schema (Streams page now lives under Project Settings)
const streamTypeSchema = z.object({
	type: z.enum(['all']),
})

const MetadataTypeSchema = z.object({
	type: z.enum(['all']),
})

const apiTypeSchema = z.object({
	type: z.enum(['all']).catch('all'),
})

// DASHBOARDS (under Data section)
const dashboardTypeSchema = z.object({
	type: z.enum(['all', 'device', 'fleet']),
})

const dashboardsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/data/dashboards',
	component: () => (
		<RouteAccessBoundary resource='dashboards'>
			<Outlet />
		</RouteAccessBoundary>
	),
})

// Index route for dashboards that redirects to all
const dashboardsIndexRoute = createRoute({
	getParentRoute: () => dashboardsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/data/dashboards/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})

// Dashboard with type parameter
const dashboardsTypeRoute = createRoute({
	getParentRoute: () => dashboardsRoute,
	path: '/$type',
	parseParams: params => dashboardTypeSchema.parse(params),
	validateSearch: dashboardSearchSchema,
	component: () => <DashboardsPage />,
})

// Dashboard Display route - for showing dashboard panels with selected device OR device selector
const dashboardDisplayParamsSchema = z.object({
	dashboardId: z.string(),
	deviceId: z.string(),
})

const dashboardDisplayRoute = createRoute({
	getParentRoute: () => dashboardsRoute,
	path: '/view/$dashboardId/$deviceId',
	parseParams: params => dashboardDisplayParamsSchema.parse(params),
	component: () => <DashboardDisplayPage />,
	beforeLoad: ({ context }) => {
		authenticatedRouteGuard(context)
	},
})

// DBC PARSERS (under Data section)
const dbcParserTypeSchema = z.object({
	type: z.enum(['all', 'custom', 'obd', 'j1939']),
})

const dbcParsersRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/data/dbc-parsers',
	component: () => (
		<RouteAccessBoundary resource='dbc_parsers'>
			<Outlet />
		</RouteAccessBoundary>
	),
})

// Index route for dbc-parsers that redirects to all
const dbcParsersIndexRoute = createRoute({
	getParentRoute: () => dbcParsersRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/data/dbc-parsers/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})

// DBC parsers with type parameter
const dbcParsersTypeRoute = createRoute({
	getParentRoute: () => dbcParsersRoute,
	path: '/$type',
	parseParams: params => dbcParserTypeSchema.parse(params),
	validateSearch: dbcParsersSearchSchema,
	component: () => <DbcParserManagementPage />,
})

const exportersRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/data/exporters',
	component: () => (
		<RouteAccessBoundary resource='exporters'>
			<Outlet />
		</RouteAccessBoundary>
	),
})

const exportersIndexRoute = createRoute({
	getParentRoute: () => exportersRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/data/exporters/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})

// Exporters with type parameter
const exportersTypeRoute = createRoute({
	getParentRoute: () => exportersRoute,
	path: '/$type',
	component: () => <ExportersPage />,
})

// TERMINAL (under Data section)
const terminalRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/data/terminal',
	component: () => <TerminalPage />,
})

// ACTION RULES
const rulesRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/actions/rules',
	component: () => <Outlet />,
})

// Index route for rules that redirects to all
const rulesIndexRoute = createRoute({
	getParentRoute: () => rulesRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/actions/rules/$status',
			params: { orgId: params.orgId, tenantId: params.tenantId, status: 'all' },
			search,
			replace: true,
		})
	},
})

const rulesStatusRoute = createRoute({
	getParentRoute: () => rulesRoute,
	path: '/$status',
	component: () => <RulesPage />,
})

const remoteCommandsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/actions/remote-commands',
	component: () => <Outlet />,
})

// Index route for remote commands that redirects to all
const remoteCommandsIndexRoute = createRoute({
	getParentRoute: () => remoteCommandsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/actions/remote-commands/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})

const remoteCommandsTypeRoute = createRoute({
	getParentRoute: () => remoteCommandsRoute,
	path: '/$type',
	component: () => <RemoteCommandsPage />,
})

const otaCampaignsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/actions/ota-campaigns',
	component: () => <Outlet />,
})

const otaCampaignsIndexRoute = createRoute({
	getParentRoute: () => otaCampaignsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/actions/ota-campaigns/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})

const otaCampaignsTypeRoute = createRoute({
	getParentRoute: () => otaCampaignsRoute,
	path: '/$type',
	component: () => <OtaCampaignsPage />,
})

// ASSETS
const assetsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/actions/assets',
	component: () => <Outlet />,
})

// Index route for assets that redirects to files
const assetsIndexRoute = createRoute({
	getParentRoute: () => assetsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/actions/assets/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'files' },
			search,
			replace: true,
		})
	},
})

// Assets with type parameter
const assetsTypeRoute = createRoute({
	getParentRoute: () => assetsRoute,
	path: '/$type',
	component: () => <AssetsPage />,
})

// MONITORING ALERT RULES (under Monitoring section)
const sessionRuleTypeSchema = z.object({
	type: z.enum(['all', 'no-signal', 'signal-based']),
})

const sessionsRulesRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/sessions/rules',
	component: () => <Outlet />,
})

// Index route for session rules that redirects to all
const sessionsRulesIndexRoute = createRoute({
	getParentRoute: () => sessionsRulesRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/sessions/rules/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})

// Session rules with type parameter
const sessionsRulesTypeRoute = createRoute({
	getParentRoute: () => sessionsRulesRoute,
	path: '/$type',
	parseParams: params => sessionRuleTypeSchema.parse(params),
	validateSearch: sessionRulesSearchSchema,
	component: () => <SessionRulesPage />,
})

// Sessions dashboard route
const sessionsDashboardRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/sessions/dashboard',
	component: () => <Outlet />,
})

// Index route for sessions dashboard that redirects to all
const sessionsDashboardIndexRoute = createRoute({
	getParentRoute: () => sessionsDashboardRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/sessions/dashboard/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})

const sessionsDashboardTypeSchema = z.object({
	// 'all' or a session rule name (URL-decoded by the router)
	type: z.string().min(1),
})

// Sessions dashboard with type parameter
const sessionsDashboardTypeRoute = createRoute({
	getParentRoute: () => sessionsDashboardRoute,
	path: '/$type',
	parseParams: params => sessionsDashboardTypeSchema.parse(params),
	component: () => <SessionsFeedPage />,
})

// SYSTEM NOTIFICATIONS
const systemNotificationsTypeSchema = z.object({
	type: z.enum(['all', 'dbc', 'exporters', 'notifications']),
})

const systemNotificationsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/system-notifications',
	component: () => <Outlet />,
})

const systemNotificationsIndexRoute = createRoute({
	getParentRoute: () => systemNotificationsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/system-notifications/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})

const systemNotificationsTypeRoute = createRoute({
	getParentRoute: () => systemNotificationsRoute,
	path: '/$type',
	parseParams: params => systemNotificationsTypeSchema.parse(params),
	component: () => <SystemNotificationsPage />,
})

// Audit Logs
const auditLogsRouteSchema = z.object({
	type: z.enum(['all']),
})

const auditLogsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/audits/audit-logs',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})

// Index route for audit logs that redirects to all
const auditLogsIndexRoute = createRoute({
	getParentRoute: () => auditLogsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/audits/audit-logs/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})

// Audit logs with type parameter
const auditLogsTypeRoute = createRoute({
	getParentRoute: () => auditLogsRoute,
	path: '/$type',
	parseParams: params => auditLogsRouteSchema.parse(params),
	component: () => <AuditLogsPage />,
	beforeLoad: ({ context, location }) => {
		authenticatedRouteGuard(context, { location })
	},
})

// Organization-level Audit Logs
const orgAuditLogsRouteSchema = z.object({
	type: z.string().default('all'),
})

const organizationAuditLogsRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/audits/audit-logs',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		organizationLevelRouteGuard(context, params.orgId)
		useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(params.orgId)
	},
})

const organizationAuditLogsIndexRoute = createRoute({
	getParentRoute: () => organizationAuditLogsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/settings/audits/audit-logs/$type',
			params: { orgId: params.orgId, type: 'all' },
			search,
			replace: true,
		})
	},
})

const organizationAuditLogsTypeRoute = createRoute({
	getParentRoute: () => organizationAuditLogsRoute,
	path: '/$type',
	parseParams: params => orgAuditLogsRouteSchema.parse(params),
	component: () => <AuditLogsPage />,
})

// Organization-level Routes (moved from project level)
const organizationInventoryRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/inventory',
	component: () => <Outlet />,
	beforeLoad: async ({ context, params, location }) => {
		platformOnlyRouteGuard(context)
		organizationLevelRouteGuard(context, params.orgId)
		useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(params.orgId)

		let rootTenantId = useProjectsStore.getState().rootTenantId
		if (!rootTenantId) {
			try {
				await useProjectsStore.getState().fetchProjects(undefined, undefined, undefined)
			} catch {
				// fetchProjects sets its own error state; proceed with whatever rootTenantId is available
			}
			rootTenantId = useProjectsStore.getState().rootTenantId
		}

		const { user, currentTenant } = useAuthStore.getState()
		const quotaTenantId = resolveQuotaTenantId({
			currentTenant,
			pathname: location.pathname,
			rootTenantId,
			organizationId: params.orgId,
			user,
		})
		await prefetchQuota(quotaTenantId ?? undefined)
	},
})

// Index route for inventory that redirects to all
const organizationInventoryIndexRoute = createRoute({
	getParentRoute: () => organizationInventoryRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/inventory/$status',
			params: { orgId: params.orgId, status: DeviceStatus.All },
			search,
			replace: true,
		})
	},
})

// Inventory with status parameter
const organizationInventoryStatusRoute = createRoute({
	getParentRoute: () => organizationInventoryRoute,
	path: '/$status',
	parseParams: params => statusParamsSchema.parse(params),
	validateSearch: deviceSearchSchema,
	component: () => <DeviceManagementPage />,
})

const organizationMetersRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/meters',
	component: () => <OrganizationMetersLayout />,
	beforeLoad: ({ context, params }) => {
		// meters route should be available only for platform deployments
		platformOnlyRouteGuard(context)
		organizationLevelRouteGuard(context, params.orgId)
		useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(params.orgId)
	},
})

const organizationMetersIndexRoute = createRoute({
	getParentRoute: () => organizationMetersRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/meters/$module',
			params: { orgId: params.orgId, module: 'devices' },
			search,
			replace: true,
		})
	},
})

const metersModuleSchema = z.object({
	module: z.enum(METERS_MODULE_IDS),
})

const organizationMetersModuleRoute = createRoute({
	getParentRoute: () => organizationMetersRoute,
	path: '/$module',
	parseParams: params => metersModuleSchema.parse(params),
	component: () => {
		const { module } = organizationMetersModuleRoute.useParams()
		return <OrganizationMetersList module={module as MetersModule} />
	},
})

const organizationOperationsRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/operations',
	component: () => <Outlet />,
	beforeLoad: async ({ context, params }) => {
		platformOnlyRouteGuard(context)
		organizationLevelRouteGuard(context, params.orgId)
		useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(params.orgId)

		// Detach/attach need allProjects loaded to resolve the root project.
		if (!useProjectsStore.getState().rootTenantId) {
			try {
				await useProjectsStore.getState().fetchProjects(undefined, undefined, undefined)
			} catch {
				// fetchProjects sets its own error state; proceed with whatever is available
			}
		}
	},
})

const organizationOperationsIndexRoute = createRoute({
	getParentRoute: () => organizationOperationsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/operations/$type',
			params: { orgId: params.orgId, type: 'all' },
			search,
			replace: true,
		})
	},
})

const organizationOperationsTypeRoute = createRoute({
	getParentRoute: () => organizationOperationsRoute,
	path: '/$type',
	component: () => <RemoteCommandsPage />,
})

// SETTINGS (under tenant project route)
const settingsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/settings',
	component: () => <SettingsPage />,
	// Admin gating is per-tab below (general/users/roles/quotas/audit-logs/api-keys); the
	// RBAC-gated tabs (metadata/streams/action-settings/subscriber-groups) stay open to non-admins.
	beforeLoad: ({ context }) => {
		authenticatedRouteGuard(context)
	},
})

// Index route for settings that redirects to general
const settingsIndexRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/general',
			params: { orgId: params.orgId, tenantId: params.tenantId },
			search,
			replace: true,
		})
	},
})

const generalSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/general',
	component: () => (
		<RouteAccessBoundary resource='project_settings'>
			<ProjectGeneralPage />
		</RouteAccessBoundary>
	),
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})

const ApiKeysSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/api-keys',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})
const settingsApiKeysIndexRoute = createRoute({
	getParentRoute: () => ApiKeysSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/api-keys/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})
const settingsApiKeysTypeRoute = createRoute({
	getParentRoute: () => ApiKeysSettingsRoute,
	path: '/$type',
	parseParams: params => apiTypeSchema.parse(params),
	component: () => <ApiKeysSettingsPage />,
})

const userTypeSchema = z.object({
	type: z.string().default('all'),
})
const UsersSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/users',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})
const settingsUsersIndexRoute = createRoute({
	getParentRoute: () => UsersSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/users/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})
// Add users with type parameter:
const settingsUsersTypeRoute = createRoute({
	getParentRoute: () => UsersSettingsRoute,
	path: '/$type',
	parseParams: params => userTypeSchema.parse(params),
	component: () => <UsersSettingsPage />,
})

const roleTypeSchema = z.object({
	type: z.enum(['all']),
})
const RoleSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/roles',
	component: () => <Outlet />,
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})
const settingsRoleIndexRoute = createRoute({
	getParentRoute: () => RoleSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/roles/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})
const settingsRoleTypeRoute = createRoute({
	getParentRoute: () => RoleSettingsRoute,
	path: '/$type',
	parseParams: params => roleTypeSchema.parse(params),
	component: () => <RoleSettingsPage />,
})

const QuotasSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/quotas',
	component: () => (
		<RouteAccessBoundary resource='project_settings'>
			<QuotasPage />
		</RouteAccessBoundary>
	),
	beforeLoad: ({ context, params }) => {
		projectSettingsAdminRouteGuard(context, params)
	},
})

// SETTINGS — Metadata (moved from Data section)
const settingsMetadataRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/metadata',
	// No route-level boundary: MetadataPage stays mounted so its header is kept; the body
	// self-restricts via per-resource RBAC. See MetadataPage.
	component: () => <Outlet />,
})
const settingsMetadataIndexRoute = createRoute({
	getParentRoute: () => settingsMetadataRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/metadata/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})
const settingsMetadataTypeRoute = createRoute({
	getParentRoute: () => settingsMetadataRoute,
	path: '/$type',
	parseParams: params => MetadataTypeSchema.parse(params),
	component: () => <MetadataPage />,
})

// SETTINGS — Streams (moved from Data section)
const settingsStreamsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/streams',
	// No route-level boundary: StreamsPage stays mounted so its header is kept; the body
	// self-restricts via per-resource RBAC. See StreamsPage.
	component: () => <Outlet />,
})
const settingsStreamsIndexRoute = createRoute({
	getParentRoute: () => settingsStreamsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/streams/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})
const settingsStreamsTypeRoute = createRoute({
	getParentRoute: () => settingsStreamsRoute,
	path: '/$type',
	parseParams: params => streamTypeSchema.parse(params),
	validateSearch: streamsSearchSchema,
	component: () => <StreamsPage />,
})

// SETTINGS — Action Settings (moved from Actions section)
const settingsActionSettingsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/action-settings',
	// No route-level boundary: ActionSettingsPage stays mounted so its header is kept, and the body
	// self-restricts via per-resource RBAC. See ActionSettingsPage.
	component: () => <Outlet />,
})
const settingsActionSettingsIndexRoute = createRoute({
	getParentRoute: () => settingsActionSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/action-settings/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'active' },
			search,
			replace: true,
		})
	},
})
const settingsActionSettingsTypeRoute = createRoute({
	getParentRoute: () => settingsActionSettingsRoute,
	path: '/$type',
	component: () => <ActionSettingsPage />,
})

// SETTINGS — Subscriber Groups (moved from Sessions section)
const settingsSubscriberGroupsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/subscriber-groups',
	// No route-level boundary: SubscriberGroupsPage stays mounted so its header is kept; the body
	// self-restricts via per-resource RBAC. See SubscriberGroupsPage.
	component: () => <Outlet />,
})
const settingsSubscriberGroupsIndexRoute = createRoute({
	getParentRoute: () => settingsSubscriberGroupsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/settings/subscriber-groups/$type',
			params: { orgId: params.orgId, tenantId: params.tenantId, type: 'all' },
			search,
			replace: true,
		})
	},
})
const settingsSubscriberGroupsTypeRoute = createRoute({
	getParentRoute: () => settingsSubscriberGroupsRoute,
	path: '/$type',
	component: () => <SubscriberGroupsPage />,
})

const diagnosticsRoute = createRoute({
	getParentRoute: () => settingsRoute,
	path: '/diagnostics',
	component: () => <DiagnosticsPage />,
	beforeLoad: ({ context }) => {
		authenticatedRouteGuard(context)
	},
})

// ORGANIZATION-LEVEL SETTINGS
const orgSettingsRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/settings',
	component: () => <SettingsPage />,
	beforeLoad: ({ context, params }) => {
		platformOnlyRouteGuard(context)
		useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(params.orgId)
	},
})

const orgSettingsIndexRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/settings/general',
			params: { orgId: params.orgId },
			search,
			replace: true,
		})
	},
})

const orgGeneralSettingsRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/general',
	component: () => <OrganizationGeneralPage />,
})

const orgUsersSettingsRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/users',
	component: () => <Outlet />,
})
const orgSettingsUsersIndexRoute = createRoute({
	getParentRoute: () => orgUsersSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/settings/users/$type',
			params: { orgId: params.orgId, type: 'all' },
			search,
			replace: true,
		})
	},
})
const orgSettingsUsersTypeRoute = createRoute({
	getParentRoute: () => orgUsersSettingsRoute,
	path: '/$type',
	parseParams: params => userTypeSchema.parse(params),
	component: () => <UsersSettingsPage />,
})

const orgApiKeysSettingsRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/api-keys',
	component: () => <Outlet />,
	beforeLoad: async () => {
		const { rootTenantId } = useProjectsStore.getState()
		if (!rootTenantId) {
			try {
				await useProjectsStore.getState().fetchProjects(undefined, undefined, undefined)
			} catch {
				// fetchProjects sets its own error state; proceed with whatever rootTenantId is available
			}
		}
	},
})

const orgSettingsApiKeysIndexRoute = createRoute({
	getParentRoute: () => orgApiKeysSettingsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/settings/api-keys/$type',
			params: { orgId: params.orgId, type: 'all' },
			search,
			replace: true,
		})
	},
})

const orgSettingsApiKeysTypeRoute = createRoute({
	getParentRoute: () => orgApiKeysSettingsRoute,
	path: '/$type',
	parseParams: params => apiTypeSchema.parse(params),
	component: () => <ApiKeysSettingsPage />,
})

// ORGANIZATION SETTINGS — Metadata (moved from Inventory)
const orgSettingsMetadataRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/metadata',
	// No route-level boundary: MetadataPage stays mounted so its header is kept; the body
	// self-restricts via per-resource RBAC. See MetadataPage.
	component: () => <Outlet />,
	beforeLoad: async () => {
		// MetadataPage passes rootTenantId explicitly as x-bytebeam-tenant since this
		// org-level URL has no tenant segment. Ensure it's loaded.
		const { rootTenantId } = useProjectsStore.getState()
		if (!rootTenantId) {
			try {
				await useProjectsStore.getState().fetchProjects(undefined, undefined, undefined)
			} catch {
				// fetchProjects sets its own error state; proceed with whatever rootTenantId is available
			}
		}
	},
})
const orgSettingsMetadataIndexRoute = createRoute({
	getParentRoute: () => orgSettingsMetadataRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/settings/metadata/$type',
			params: { orgId: params.orgId, type: 'all' },
			search,
			replace: true,
		})
	},
})
const orgSettingsMetadataTypeRoute = createRoute({
	getParentRoute: () => orgSettingsMetadataRoute,
	path: '/$type',
	parseParams: params => MetadataTypeSchema.parse(params),
	component: () => <MetadataPage />,
})

// ORGANIZATION SETTINGS — Streams
const orgSettingsStreamsRoute = createRoute({
	getParentRoute: () => orgSettingsRoute,
	path: '/streams',
	component: () => <Outlet />,
	beforeLoad: async () => {
		// StreamsPage passes rootTenantId explicitly as x-bytebeam-tenant since this
		// org-level URL has no tenant segment. Ensure it's loaded.
		const { rootTenantId } = useProjectsStore.getState()
		if (!rootTenantId) {
			try {
				await useProjectsStore.getState().fetchProjects(undefined, undefined, undefined)
			} catch {
				// fetchProjects sets its own error state; proceed with whatever rootTenantId is available
			}
		}
	},
})
const orgSettingsStreamsIndexRoute = createRoute({
	getParentRoute: () => orgSettingsStreamsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/settings/streams/$type',
			params: { orgId: params.orgId, type: 'all' },
			search,
			replace: true,
		})
	},
})
const orgSettingsStreamsTypeRoute = createRoute({
	getParentRoute: () => orgSettingsStreamsRoute,
	path: '/$type',
	parseParams: params => z.object({ type: z.string().default('all') }).parse(params),
	component: () => <StreamsPage />,
})

// Create the route tree
const routeTree = rootRoute.addChildren([
	// Basic routes
	loginRoute,
	welcomeRoute,
	globalAccessDeniedRoute,
	panelContainerPreviewRoute, // TEMPORARY — see comment at its definition above.
	organizationRoute.addChildren([
		organizationBillingRoute.addChildren([organizationBillingIndexRoute, organizationBillingTypeRoute]),
		organizationAccessDeniedRoute,
		// Organization-level routes (inventory, meters, operations, audits)
		organizationInventoryRoute.addChildren([organizationInventoryIndexRoute, organizationInventoryStatusRoute]),
		organizationMetersRoute.addChildren([organizationMetersIndexRoute, organizationMetersModuleRoute]),
		organizationOperationsRoute.addChildren([organizationOperationsIndexRoute, organizationOperationsTypeRoute]),
		orgSettingsRoute.addChildren([
			orgSettingsIndexRoute,
			orgGeneralSettingsRoute,
			orgUsersSettingsRoute.addChildren([orgSettingsUsersIndexRoute, orgSettingsUsersTypeRoute]),
			orgApiKeysSettingsRoute.addChildren([orgSettingsApiKeysIndexRoute, orgSettingsApiKeysTypeRoute]),
			orgSettingsMetadataRoute.addChildren([orgSettingsMetadataIndexRoute, orgSettingsMetadataTypeRoute]),
			orgSettingsStreamsRoute.addChildren([orgSettingsStreamsIndexRoute, orgSettingsStreamsTypeRoute]),
			organizationAuditLogsRoute.addChildren([organizationAuditLogsIndexRoute, organizationAuditLogsTypeRoute]),
		]),
		organizationProjectRoute.addChildren([
			projectHomeRoute,
			accessDeniedRoute,

			// Device Management
			devicesRoute.addChildren([devicesIndexRoute, devicesStatusRoute]),

			// Data - Dashboards
			dashboardsRoute.addChildren([dashboardsIndexRoute, dashboardsTypeRoute, dashboardDisplayRoute]),

			// Data - DBC Parsers
			dbcParsersRoute.addChildren([dbcParsersIndexRoute, dbcParsersTypeRoute]),
			// Data - Exporters
			exportersRoute.addChildren([exportersIndexRoute, exportersTypeRoute]),

			// Data - Terminal
			terminalRoute,

			// Actions - Rules
			rulesRoute.addChildren([rulesIndexRoute, rulesStatusRoute]),

			// Actions - Remote Commands
			remoteCommandsRoute.addChildren([remoteCommandsIndexRoute, remoteCommandsTypeRoute]),

			// Actions - OTA Campaigns
			otaCampaignsRoute.addChildren([otaCampaignsIndexRoute, otaCampaignsTypeRoute]),

			// Actions - Assets
			assetsRoute.addChildren([assetsIndexRoute, assetsTypeRoute]),

			// sessions center
			sessionsRulesRoute.addChildren([sessionsRulesIndexRoute, sessionsRulesTypeRoute]),
			sessionsDashboardRoute.addChildren([sessionsDashboardIndexRoute, sessionsDashboardTypeRoute]),

			systemNotificationsRoute.addChildren([systemNotificationsIndexRoute, systemNotificationsTypeRoute]),

			settingsRoute.addChildren([
				generalSettingsRoute,
				ApiKeysSettingsRoute,
				UsersSettingsRoute,
				settingsUsersIndexRoute,
				settingsUsersTypeRoute,
				RoleSettingsRoute,
				settingsApiKeysIndexRoute,
				settingsApiKeysTypeRoute,
				settingsRoleIndexRoute,
				settingsRoleTypeRoute,
				QuotasSettingsRoute,
				diagnosticsRoute,
				settingsMetadataRoute.addChildren([settingsMetadataIndexRoute, settingsMetadataTypeRoute]),
				settingsStreamsRoute.addChildren([settingsStreamsIndexRoute, settingsStreamsTypeRoute]),
				settingsActionSettingsRoute.addChildren([settingsActionSettingsIndexRoute, settingsActionSettingsTypeRoute]),
				settingsSubscriberGroupsRoute.addChildren([
					settingsSubscriberGroupsIndexRoute,
					settingsSubscriberGroupsTypeRoute,
				]),
				settingsIndexRoute,
				auditLogsRoute.addChildren([auditLogsIndexRoute, auditLogsTypeRoute]),
			]),
		]),
	]),
	indexRoute,
	notFoundRoute,
])

const ROUTE_SKELETON_SHAPES: Record<
	string,
	{
		checkbox?: boolean
		pager?: boolean
		display?: boolean
		cols?: number
		toolbar?: boolean
		body?: 'table' | 'settings'
		cards?: number
	}
> = {
	'/audits/audit-logs': { checkbox: false },
	'/sessions/dashboard': { checkbox: false },
	'/system-notifications': { checkbox: false, display: false },
	'/diagnostics': { checkbox: false, pager: false, display: false },
	'/meters': { checkbox: false, pager: false, display: false },
	'/actions/remote-commands': { pager: false },
	'/operations': { cols: 7 },
	'/actions/ota-campaigns': { pager: false },
	'/settings/streams': { cols: 2, toolbar: false },
	'/settings/metadata': { cols: 2, display: false },
	'/settings/users': { cols: 3, display: false },
	'/settings/roles': { display: false },
	'/settings/subscriber-groups': { display: false },
	'/settings/api-keys': { cols: 6, display: false },
	'/data/exporters': { cols: 6 },
	'/actions/assets': { cols: 6, display: false },
	'/device-management': { toolbar: false },
	'/inventory': { toolbar: false, cols: 3 },
	'/settings/general': { body: 'settings', cards: 4 },
	'/settings/quotas': { body: 'settings', cards: 1 },
}

const REMOTE_COMMANDS_ROUTES = ['/actions/remote-commands', '/operations']

function RoutePendingSkeleton() {
	const pathname = window.location.pathname
	const shape = Object.entries(ROUTE_SKELETON_SHAPES).find(([route]) => pathname.includes(route))?.[1]
	const [remoteCommandsGroupBy] = useDisplayPreference<string | null>('remoteCommands', 'groupBy', 'status')
	if (shape?.body === 'settings') {
		return (
			<CardGridSkeleton
				count={shape.cards ?? 3}
				columns={1}
				lines={3}
				className='w-full max-w-4xl mx-auto px-4 mt-4 min-h-0 flex-1 auto-rows-fr'
			/>
		)
	}
	const hasPager = REMOTE_COMMANDS_ROUTES.some(route => pathname.includes(route))
		? remoteCommandsGroupBy === null
		: (shape?.pager ?? true)
	return (
		<PageShellSkeleton
			withHeader={false}
			withToolbar={shape?.toolbar ?? true}
			withCheckbox={shape?.checkbox ?? true}
			withPager={hasPager}
			withDisplay={shape?.display ?? true}
			cols={shape?.cols}
		/>
	)
}

// Create the router
export const router = createRouter({
	routeTree,
	defaultNotFoundComponent: () => <NotFoundPage />,
	defaultPendingComponent: () => <RoutePendingSkeleton />,
})

// Export router type for type safety
export type AppRouter = typeof router

// Register the router for type safety
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}
